import React, { useContext, useEffect, useState } from "react";

// Constants
import {
  vehicleTypes,
  directionalityOptions,
  mapFocusCategories,
  geographicChoices,
  yearOptions,
  YEARS_WITHOUT_DIRECTIONAL,
} from "../common/constants";

// Context
import { useGlobalState, useGlobalUpdater } from "../common/GlobalContext";

// Material UI - core
import Typography from "@material-ui/core/Typography";
import PropTypes from "prop-types";

// Material UI - MUI v5
import Box from "@mui/material/Box";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ThemeProvider, createTheme } from "@mui/material/styles";

// Local components
import GeneralFilter from "../map/GeneralFilter";
import { sidebarStyles } from "../ui/styles";

const Sidebar = (props) => {
  const { mode } = props;

  const theme = createTheme({
    components: {
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            color: mode === "dark" ? "white" : "",
            "&:hover": {
              "&& fieldset": {
                border: "3px solid green",
              },
            },
          },
          notchedOutline: {
            borderColor: mode === "dark" ? "white" : "#1E1E1E",
            color: mode === "dark" ? "white" : "",
          },
          input: {
            color: mode === "dark" ? "white" : "",
          },
        },
      },
      MuiInputLabel: {
        styleOverrides: {
          root: { color: mode === "dark" ? "white" : "" },
        },
      },
      MuiFormControl: {
        styleOverrides: {
          root: { color: mode === "dark" ? "white" : "" },
          label: { color: mode === "dark" ? "white" : "" },
        },
      },
      MuiSelect: {
        styleOverrides: {
          select: {
            whiteSpace: "normal",
            fontSize: 14,
            color: mode === "dark" ? "white" : "",
          },
          icon: {
            color: mode === "dark" ? "white" : "",
          },
        },
      },
      MuiList: {
        styleOverrides: {
          root: {
            color: mode === "dark" ? "white" : "",
            backgroundColor: mode === "dark" ? "#b28c27" : "",
          },
        },
      },

      MuiAccordion: {
        styleOverrides: {
          rounded: {
            color: mode === "dark" ? "white" : "",
            backgroundColor: mode === "dark" ? "#1E1E1E" : "",
          },
          root: {
            border: mode === "dark" ? `1px solid white` : `1px solid #1E1E1E`,
          },
        },
      },
      MuiAccordionSummary: {
        styleOverrides: {
          content: {
            color: mode === "dark" ? "white" : "",
          },
        },
      },
      MuiSvgIcon: {
        styleOverrides: {
          root: {
            color: mode === "dark" ? "white" : "",
            borderColor: mode === "dark" ? "white" : "",
          },
        },
      },
    },
  });

  const query = useGlobalState();
  const dispatch = useGlobalUpdater();
  // console.log("mode-check", mode);

  const classes = sidebarStyles();



  const handleChange = (event) => {
    dispatch({
      type: event.target.name,
      selection: event.target.value,
    });
  };

  let setOfGeoCategories = new Set(
    geographicChoices.map((itm) => itm.category)
  );

  const handleChangeLayerCategory = (event) => {
    const newCategory = event.target.value;
    const firstLayerInCategory = geographicChoices.find(
      (itm) => itm.category === newCategory
    );

    dispatch({
      type: event.target.name,
      selection: {
        category: newCategory,
        layer: firstLayerInCategory ? firstLayerInCategory.text : query.layerSpecs.layer,
      },
    });
    setOpenLayerSelect(!openLayerSelect);
  };

  const handleChangeLayerClose = (event) => {
    if (event.target.textContent === "") {
      // Menu closed without a new selection (click-away / Escape). Re-dispatch
      // the current layer to keep state consistent. Guard the lookup: if the
      // current layer isn't found in geographicChoices (renamed/removed/typo),
      // .filter(...)[0] would be undefined and crash. Fall back to the
      // existing category so this stays a safe no-op.
      const match = geographicChoices.find(
        (itm) => itm.text === query.layerSpecs.layer
      );
      dispatch({
        type: "setLayerSpecs",
        selection: {
          category: match ? match.category : query.layerSpecs.category,
          layer: query.layerSpecs.layer,
        },
      });
    } else {
      dispatch({
        type: "setLayerSpecs",
        selection: {
          category: query.layerSpecs.category,
          layer: event.target.textContent,
        },
      });

      let item = geographicChoices.filter(
        (itm) => itm.text === event.target.textContent
      )[0];
      dispatch({
        type: "setGeoRoute",
        selection: item.geoRoute,
      });

      dispatch({
        type: "setGeography",
        selection: {
          region: item.text
            .replace(/\s+/g, "-")
            .replace("'", "")
            .replace(".", "")
            .toLowerCase(),
          county: item.text
            .replace(/\s+/g, "-")
            .replace("'", "")
            .replace(".", ""),
        },
      });
      if (item.category === "Region")
        dispatch({
          type: "setGeography",
          selection: {
            region: item.text
              .replace(/\s+/g, "-")
              .replace("'", "")
              .replace(".", "")
              .toLowerCase(),
            county: "None",
          },
        });
      if (item.category === "County")
        dispatch({
          type: "setGeography",
          selection: {
            region: "None",
            county: item.text
              .replace(/\s+/g, "-")
              .replace("'", "")
              .replace(".", ""),
          },
        });
      dispatch({
        type: "setSegments",
        selection: {
          segmentsTable: item.segmentsTable,
          segmentSelection: item.segmentSelection,
        },
      });
      //  check if current mapFocus is still valid
      const newGeoRoute = item.geoRoute;
      const isMapFocusStillValid = mapFocusCategories.some(
        (itm) => itm.category === newGeoRoute && itm.description === query.mapFocus
      );

      // Save previous mapFocus before resetting
      dispatch({
        type: "setPreviousMapFocus",
        selection: query.mapFocus,
      });

      dispatch({
        type: "setMapFocus",
        selection: isMapFocusStillValid ? query.mapFocus : "None",
      });
      if (
        item.text === "Top 100 Truck Bottlenecks" ||
        item.text === "Directional Top 100 Truck Bottlenecks"
      ) {
        dispatch({
          type: "setVehicleType",
          selection: "truck",
        });
      } else {
        if (
          item.text === "Top 100 All-Vehicle Bottlenecks" ||
          item.text === "Directional Top 100 All-Vehicle Bottlenecks"
        ) {
          dispatch({
            type: "setVehicleType",
            selection: "all",
          });
        }
      }
      if (
        item.text === "Directional Top 100 All-Vehicle Bottlenecks" ||
        item.text === "Directional Top 100 Truck Bottlenecks"
      ) {
        dispatch({
          type: "setDirectionality",
          selection: "directional",
        });
      } else {
        if (
          item.text === "Top 100 All-Vehicle Bottlenecks" ||
          item.text === "Top 100 Truck Bottlenecks"
        ) {
          dispatch({
            type: "setDirectionality",
            selection: "crossSectional",
          });
        }
      }
      // Only reset features if the geography actually changed
      if (item.text !== query.layerSpecs.layer) {
        dispatch({
          type: "setFeatures",
          selection: [],
        });
      }
    }
    setOpenLayerSelect(!openLayerSelect);
    console.log("geoRoute", query.geoRoute);
  };

  const handleChangeLayerOpen = (event) => {
    setOpenLayerSelect(!openLayerSelect);
  };

  const [openLayerSelect, setOpenLayerSelect] = React.useState(false);
  const [filterSelected, setFilterSelected] = React.useState(false);

  useEffect(() => {
    let filterCheck = false;
    if (
      query.layerSpecs.category === "Top 100"
    ) {
      query.top100Filters.map((itm) => {
        if (!itm.checked) filterCheck = true;
      });
      query.truckTop100Filters.map((itm) => {
        if (!itm.checked) filterCheck = true;
      });
    }
    if (
      query.layerSpecs.category !== "Top 100"
    ) {
      query.ownershipFilters.map((itm) => {
        if (!itm.checked) filterCheck = true;
      });
      query.fsystemFilters.map((itm) => {
        if (!itm.checked) filterCheck = true;
      });
      query.urbanCodeFilters.map((itm) => {
        if (!itm.checked) filterCheck = true;
      });
      query.nhsFilters.map((itm) => {
        if (!itm.checked) filterCheck = true;
      });
      query.strahnetFilters.map((itm) => {
        if (!itm.checked) filterCheck = true;
      });
      query.truckNetworkFilters.map((itm) => {
        if (!itm.checked) filterCheck = true;
      });
    }


    setFilterSelected(filterCheck);
  }, [
    query.top100Filters,
    query.truckTop100Filters,
    query.ownershipFilters,
    query.fsystemFilters,
    query.urbanCodeFilters,
    query.nhsFilters,
    query.strahnetFilters,
    query.truckNetworkFilters,
    query.layerSpecs.category,
  ]);
//auto populated the table on adding segments to selection
useEffect(() => {
  if (query.userSelectedSegments && query.userSelectedSegments.length > 0) {
    dispatch({
      type: "setReportType",
      selection: "User Selected Segments",
    });
  } else {
    dispatch({
      type: "setReportType",
      selection: "Summary",
    });
  }
}, [query.userSelectedSegments]);


  const drawer2 = (
    <div className="drawer-wrapper">

      <div className="current">
        <Typography variant="subtitle1">Select Layer:</Typography>
      </div>

      <div className="current">
        <FormControl
          sx={{
            m: 0,
            minWidth: 150,
            borderColor: mode === "dark" ? "#F9F6EE" : "",
          }}
        >
          <InputLabel id="category-label" htmlFor="show-category">
            <Typography variant="subtitle1">Category:</Typography>
          </InputLabel>
          <Select
            labelId="category-label"
            id="category-id"
            value={query.layerSpecs.category}
            onChange={handleChangeLayerCategory}
            label="Category:"
            name="setLayerSpecs"
            sx={{
              width: 215,
              "& .MuiSelect-select": {
                whiteSpace: "normal",
                fontSize: 14,
                color: mode === "dark" ? "white" : "",
              },
            }}
            inputProps={{
              id: "show-category",
              "aria-label": "category",
            }}
          >

            {[...setOfGeoCategories].map((obj, index) => (
              <MenuItem value={obj} key={`sidebar-geo-cat-opt-${index}`}>
                {obj}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="current">
        <FormControl sx={{ m: 0, minWidth: 150 }}>
          <InputLabel id="layer-label" htmlFor="show-layer">
            <Typography variant="subtitle1">Layer:</Typography>
          </InputLabel>

          <Select
            labelId="layer-label"
            id="layer-id"
            value={query.layerSpecs.layer}
            label="Layer:"
            name="setLayerSpecs"
            open={openLayerSelect}
            onClose={handleChangeLayerClose}
            onOpen={handleChangeLayerOpen}
            sx={{
              width: 215,
              "& .MuiSelect-select": {
                whiteSpace: "normal",
                fontSize: 14,
              },
            }}
            inputProps={{
              id: "show-layer",
              "aria-label": "layer",
            }}
          >

            {geographicChoices
              .filter((itm) => itm.category === query.layerSpecs.category)
              .filter((itm) => {
                const isDirectional = itm.text.toLowerCase().includes("directional");
                const hideDirectional =
                  YEARS_WITHOUT_DIRECTIONAL.includes(query.yearSelected);

                return !(hideDirectional && isDirectional);
              })
              .map((col, index) => (
                <MenuItem
                  value={col.text}
                  key={`sidebar-year-opt-${index}`}

                >
                  {col.text}
                </MenuItem>
              ))}

          </Select>
        </FormControl>
      </div>

      <div className="current">
        <Typography variant="subtitle1">Select Data Preferences:</Typography>
      </div>

      <div className="current">
        <FormControl sx={{ m: 0, minWidth: 120 }}>
          <InputLabel id="year-label" htmlFor="show-year">
            <Typography variant="subtitle1">Year:</Typography>
          </InputLabel>
          <Select
            labelId="year-label"
            id="year-id"
            value={query.yearSelected}
            onChange={handleChange}
            label="Year:"
            name="setYearSelected"
            sx={{
              width: 215,
              "& .MuiSelect-select": {
                whiteSpace: "normal",
                fontSize: 14,
              },
            }}
            inputProps={{
              id: "show-year",
              "aria-label": "year",
            }}
          >

            {yearOptions.map((col, index) => (
              <MenuItem value={col.value} key={`sidebar-year-opt-${index}`}>
                {col.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="current">
        <FormControl sx={{ m: 0, minWidth: 120 }} fullWidth>
          <InputLabel id="vehicle-types-label" htmlFor="show-vehicles">
            <Typography variant="subtitle1">Vehicle Types:</Typography>
          </InputLabel>
          <Select
            labelId="vehicle-types-label"
            id="vehicle-types-id"
            value={query.vehicleType}
            onChange={handleChange}
            label="Vehicle Types:"
            name="setVehicleType"
            sx={{
              width: 215,
              "& .MuiSelect-select": {
                whiteSpace: "normal",
                fontSize: 14,
              },
            }}
            inputProps={{
              id: "show-vehicles",
              "aria-label": "vehicle-types",
            }}
          >
            {vehicleTypes.map((col, index) => (
              <MenuItem value={col.value} key={`sidebar-vtypes-opt-${index}`}>
                {col.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
      <div className="current">
        <FormControl sx={{ m: 0, minWidth: 120 }} fullWidth>
          <InputLabel id="directionality-label" htmlFor="show-directionality">
            <Typography variant="subtitle1">Directionality:</Typography>
          </InputLabel>
          <Select
            labelId="directionality-label"
            id="directionality-id"
            value={query.directionality}
            onChange={handleChange}
            label="Directionality:"
            name="setDirectionality"
            sx={{
              width: 215,
              "& .MuiSelect-select": {
                whiteSpace: "normal",
                fontSize: 14,
              },
            }}
            inputProps={{
              id: "show-directionality",
              "aria-label": "directionality",
            }}
          >
            {directionalityOptions.map((col, index) => (
              <MenuItem
                value={col.value}
                key={`sidebar-vtypes-opt-${index}`}
                disabled={col.disabled}
              >
                {col.text}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>

      <div className="current">
        <Typography variant="subtitle1">Select Map Preferences:</Typography>
      </div>

      <div className="current">
        <FormControl sx={{ m: 0, minWidth: 120 }} fullWidth>
          <InputLabel id="map-focus-label" htmlFor="show-map-focus">
            <Typography variant="subtitle1">Map Focus:</Typography>
          </InputLabel>
          <Select
            labelId="map-focus-label"
            id="map-focus-id"
            value={query.mapFocus}
            onChange={handleChange}
            label="Map Focus:"
            name="setMapFocus"
            sx={{
              width: 215,
              "& .MuiSelect-select": {
                whiteSpace: "normal",
                fontSize: 14,
              },
            }}
            inputProps={{
              id: "show-map-focus",
              "aria-label": "map-focus",
            }}
          >
            {mapFocusCategories
              .filter((itm) => itm.category === query.geoRoute)
              .map((col, index) => (
                <MenuItem value={col.description} key={`sidebar-vtypes-opt-${index}`}>
                  {col.description}
                </MenuItem>
              ))}
          </Select>

          {query.mapFocus === "None" && query.previousMapFocus && query.previousMapFocus !== "None" && (
            <div
              style={{
                color: mode === "dark" ? "#ffb74d" : "#7a3900",
                backgroundColor: mode === "dark" ? "#4a2800" : "#fff3e0",
                display: "block",
                marginTop: 4,
                padding: 8,
                borderRadius: 4,
                width: 215,
                whiteSpace: "normal",
                fontSize: 12,
                fontWeight: 700,
                boxSizing: "border-box",
              }}
            >
              "{query.previousMapFocus}" is not available for the selected layer/year.
            </div>
          )}
        </FormControl>
      </div>

      <div className="current">
        <Box
          sx={{
            width: 205,
            whiteSpace: "normal",
            mb: 2,
            mr: 0,
          }}
        >
          <Accordion>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              aria-controls="panel1a-content"
              id="panel1a-header"
              tabIndex={0}
              sx={{
                backgroundColor: filterSelected ? "#a7f175" : "",
                borderRadius: filterSelected ? 1 : "",
                // Fix: visible focus ring in both light and dark mode
                "&.Mui-focusVisible": {
                  outline: "2px solid",
                  outlineColor: (theme) =>
                    theme.palette.mode === "dark" ? "#90caf9" : "#1976d2",
                  outlineOffset: "2px",
                },
              }}
            >
              <Typography>Select Filters:</Typography>
            </AccordionSummary>
            <AccordionDetails>
              {query.geography.region === "top-100-monitored-roads" && (
                <div style={{ fontSize: 14, margin: 0, padding: 0 }}>
                  <GeneralFilter
                    filters={query.top100Filters}
                    dispatchType={"setTop100Filters"}
                    menuTitle={"Top 100 Bottlenecks Rankings"}
                  />
                  <GeneralFilter
                    filters={query.truckTop100Filters}
                    dispatchType={"setTruckTop100Filters"}
                    menuTitle={"Truck Top 100 Bottlenecks Rankings"}
                  />
                </div>
              )}
              {(query.geography.region === "top-100-all-vehicle-bottlenecks" ||
                query.geography.region ===
                "directional-top-100-all-vehicle-bottlenecks") && (
                  <div style={{ fontSize: 14, margin: 0, padding: 0 }}>
                    <GeneralFilter
                      filters={query.top100Filters}
                      dispatchType={"setTop100Filters"}
                      menuTitle={"Top 100 Bottlenecks Rankings"}
                    />
                  </div>
                )}
              {(query.geography.region === "top-100-truck-bottlenecks" ||
                query.geography.region ===
                "directional-top-100-truck-bottlenecks") && (
                  <div style={{ fontSize: 14, margin: 0, padding: 0 }}>
                    <GeneralFilter
                      filters={query.truckTop100Filters}
                      dispatchType={"setTruckTop100Filters"}
                      menuTitle={"Truck Top 100 Bottlenecks Rankings"}
                    />
                  </div>
                )}
              {query.layerSpecs.category !== "Top 100" && (
                <div>
                  <GeneralFilter
                    filters={query.ownershipFilters}
                    dispatchType={"setOwnershipFilters"}
                    menuTitle={"Ownership"}
                  />
                  <GeneralFilter
                    filters={query.fsystemFilters}
                    dispatchType={"setFsystemFilters"}
                    menuTitle={"Functional System"}
                  />
                  <GeneralFilter
                    filters={query.urbanCodeFilters}
                    dispatchType={"setUrbanCodeFilters"}
                    menuTitle={"Urban Code"}
                  />
                  <GeneralFilter
                    filters={query.nhsFilters}
                    dispatchType={"setNhsFilters"}
                    menuTitle={"National Highway System (NHS)"}
                  />
                  <GeneralFilter
                    filters={query.strahnetFilters}
                    dispatchType={"setStrahnetFilters"}
                    menuTitle={"Strategic Highway Network (STRAHNET)"}
                  />
                  <GeneralFilter
                    filters={query.truckNetworkFilters}
                    dispatchType={"setTruckNetworkFilters"}
                    menuTitle={"National Truck Network (NN)"}
                  />
                </div>
              )}
            </AccordionDetails>
          </Accordion>
        </Box>
      </div>
      <div className="current">
        <Typography variant="subtitle1">Reporting:</Typography>
      </div>
      <div className="current">
        <FormControl sx={{ m: 0, minWidth: 120 }} fullWidth>
          <InputLabel id="report-label" htmlFor="show-report">
            <Typography variant="subtitle1">Report:</Typography>
          </InputLabel>
          <Select
            labelId="report-label"
            id="report-id"
            value={query.reportType}
            onChange={handleChange}
            label="Report:"
            name="setReportType"
            sx={{
              width: 215,
              "& .MuiSelect-select": {
                whiteSpace: "normal",
                fontSize: 14,
              },
            }}
            inputProps={{
              id: "show-report",
              "aria-label": "report"
            }}
          >
            {["Summary", "User Selected Segments"].map((col, index) => (
              <MenuItem value={col} key={`sidebar-report-opt-${index}`}>
                {col}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </div>
    </div>
  );

  return (
    <ThemeProvider theme={theme}>
      <div>{drawer2}</div>
    </ThemeProvider>
  );
};

Sidebar.propTypes = {
  style: PropTypes.object,
};

export default Sidebar;